import React, { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, MousePointerClick } from "lucide-react";
import HeroScene from "./three/HeroScene";
import { PILLARS } from "../constants/content";
import { typeformLinkProps } from "./TypeformModal";
import { isMobileViewport, scrollToPillar } from "../utils/scroll";

const Hero = () => {
    const wrapRef = useRef(null);
    const progressRef = useRef(0);
    const activeRef = useRef(0);
    const [active, setActive] = useState(0);

    const { scrollYProgress } = useScroll({
        target: wrapRef,
        offset: ["start start", "end end"],
    });

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        progressRef.current = v;
        const idx = Math.min(7, Math.max(0, Math.round(v * 7)));
        activeRef.current = idx;
        setActive(idx);
    });

    const introOpacity = useTransform(scrollYProgress, [0, 0.22], [1, 0]);
    const introY = useTransform(scrollYProgress, [0, 0.22], [0, -60]);

    const pillar = PILLARS[active];

    return (
        <section ref={wrapRef} id="top" data-testid="hero-section" className="relative h-[320vh] md:h-[420vh]">
            <div className="sticky top-0 h-screen w-full overflow-hidden">
                <div className="absolute inset-0">
                    <HeroScene progressRef={progressRef} activeRef={activeRef} />
                </div>

                {/* intro copy */}
                <motion.div
                    style={{ opacity: introOpacity, y: introY }}
                    className="pointer-events-none absolute inset-x-0 top-0 flex h-full flex-col items-center justify-start px-5 pt-28 text-center sm:pt-32 lg:items-start lg:pl-24 lg:pr-[45%] lg:text-left"
                >
                    {/* readability scrim over the 3D scene */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_85%_52%_at_50%_22%,rgba(7,4,13,0.85),transparent_75%)] lg:bg-[radial-gradient(ellipse_62%_58%_at_27%_26%,rgba(7,4,13,0.85),transparent_78%)]" />
                    <p className="font-mono-tech relative mb-5 text-[11px] uppercase tracking-[0.35em] text-neon-blue text-shadow-soft">
                        Fan Creator · by ClientsClick
                    </p>
                    <h1 className="font-display relative text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
                        YOU CREATE.
                        <br />
                        <span className="text-gradient-neon">WE SCALE.</span>
                    </h1>
                    <p className="relative mt-6 max-w-xl text-base font-medium leading-relaxed text-white/90 text-shadow-soft sm:text-lg">
                        Attract more fans, serve them better and keep them longer!
                    </p>
                    <div className="pointer-events-auto relative mt-9 flex flex-wrap items-center justify-center gap-4 lg:justify-start">
                        <a
                            {...typeformLinkProps}
                            data-testid="hero-cta-primary"
                            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-purple to-neon-pink px-7 py-3.5 text-sm font-bold text-white transition-transform duration-200 hover:scale-105 glow-pink"
                        >
                            Start Now
                            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#pillars"
                            data-testid="hero-cta-secondary"
                            className="rounded-full border border-neon-blue/40 px-7 py-3.5 text-sm font-semibold text-neon-blue transition-colors duration-200 hover:bg-neon-blue/10"
                        >
                            See the 8 Pillars
                        </a>
                    </div>
                    <p className="relative mt-10 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/60 text-shadow-soft">
                        <MousePointerClick size={13} />
                        {isMobileViewport()
                            ? "Tap the scene — drop a sphere"
                            : "Click the scene — drop a sphere into the machine"}
                    </p>
                </motion.div>

                {/* pillar tracker */}
                <div
                    data-testid="pillar-tracker"
                    className="glass-panel absolute bottom-8 left-1/2 w-[min(92vw,560px)] -translate-x-1/2 rounded-2xl px-6 py-5"
                >
                    <div className="flex items-center justify-between gap-4">
                        <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neon-pink">
                            Pillar {pillar.id} / 08
                        </span>
                        <div className="flex gap-1.5">
                            {PILLARS.map((p, i) => (
                                <button
                                    key={p.id}
                                    data-testid={`pillar-dot-${i}`}
                                    onClick={() => scrollToPillar(i)}
                                    aria-label={`Walk the robot to ${p.name}`}
                                    className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                                        i === active
                                            ? "w-6 bg-neon-pink"
                                            : "w-1.5 bg-white/20 hover:w-3 hover:bg-neon-blue/60"
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                    <h3 className="font-display mt-3 text-lg font-bold text-white sm:text-xl">{pillar.name}</h3>
                    <p className="mt-1 text-sm text-white/80">{pillar.tagline}</p>
                </div>

                {/* scroll hint */}
                <div className="absolute bottom-8 right-6 hidden flex-col items-center gap-1 text-white/35 lg:flex">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em]">Scroll</span>
                    <ChevronDown size={16} className="animate-bounce" />
                </div>
            </div>
        </section>
    );
};

export default Hero;
